import type { Pessoa } from '@sheafyield/dominio';
import { apagar, gravar, type Banco } from '@sheafyield/sync';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { estudosDaPessoa, visitasDaPessoa } from '../dados/consultas.js';
import type { DonoDasLinhas } from '../dados/dono.js';
import { Botao } from '../ds/Botao.js';
import { Folha } from '../ds/Folha.js';
import { Toast } from '../ds/Toast.js';

type Pedido = { pessoa: Pessoa; visitas: number | null; aoExcluir: () => void };
type Exclusao = { solicitar: (pessoa: Pessoa, aoExcluir: () => void) => void };
const ContextoDeExclusao = createContext<Exclusao | null>(null);
export function useExclusaoDePessoa() {
  return useContext(ContextoDeExclusao);
}

export function FolhaExcluir({
  pessoa,
  visitas,
  erro,
  ocupada,
  aoFechar,
  aoConfirmar,
}: {
  pessoa: Pessoa;
  visitas: number;
  erro: string | null;
  ocupada: boolean;
  aoFechar: () => void | false;
  aoConfirmar: () => void;
}) {
  const manter = useRef<HTMLButtonElement>(null);
  return (
    <Folha
      aberta
      titulo="Excluir pessoa?"
      focoInicial={manter}
      aoFechar={aoFechar}
    >
      <div className="ficha__formulario">
        <h3>
          {pessoa.nome.trim() || pessoa.endereco.trim() || 'Pessoa sem nome'}
        </h3>
        <p>
          {visitas > 0
            ? `Esta pessoa tem ${String(visitas)} visitas no histórico. Elas continuam no relatório dos meses em que aconteceram; o que some é a ficha.`
            : 'Esta pessoa tem estudo no histórico. Ele continua no relatório dos meses em que aconteceu; o que some é a ficha.'}
        </p>
        {erro === null ? null : <p role="alert">{erro}</p>}
        <Botao
          ref={manter}
          variante="primario"
          largura="cheia"
          aria-disabled={ocupada}
          onClick={aoFechar}
        >
          Manter pessoa
        </Botao>
        <Botao
          variante="perigo"
          largura="cheia"
          carregando={ocupada}
          onClick={aoConfirmar}
        >
          Excluir pessoa
        </Botao>
      </div>
    </Folha>
  );
}

/** Vive acima das rotas e da fronteira keyed por caminho: a ficha some ao
 * excluir, mas a região viva e seu Desfazer precisam continuar na lista.
 * Fica dentro da trava; trancar desmonta também os dados desta ação. */
export function ProvedorDeExclusao({
  banco,
  dono,
  assinarConta,
  children,
}: {
  banco: Banco;
  dono: DonoDasLinhas;
  assinarConta: (aoMudar: (user_id: string) => void) => () => void;
  children: ReactNode;
}) {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [aviso, setAviso] = useState<Pessoa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupada, setOcupada] = useState(false);
  const emCurso = useRef(false);
  const geracao = useRef(0);
  const abortarTransacao = useRef<(() => void) | null>(null);
  const contaDaAcao = useRef<string | null>(null);
  useEffect(
    () =>
      assinarConta((user_id) => {
        if (contaDaAcao.current === null || contaDaAcao.current === user_id)
          return;
        // Troca de conta invalida também uma leitura ainda pendente. Renovar
        // o token da mesma conta não dispensa o Desfazer nem a confirmação.
        geracao.current++;
        // O evento pode chegar durante um await dentro da transação. Só
        // ocultar a folha deixaria essa escrita prosseguir na conta antiga.
        abortarTransacao.current?.();
        contaDaAcao.current = null;
        setPedido(null);
        setAviso(null);
        setErro(null);
      }),
    [assinarConta],
  );
  useEffect(
    () => () => {
      geracao.current++;
      abortarTransacao.current?.();
    },
    [],
  );
  function mostrarErro(causa: unknown) {
    setErro(
      causa instanceof Error
        ? causa.message
        : 'Não deu para concluir. Tente novamente.',
    );
  }
  async function excluir(intencao: Pedido, confirmada: boolean) {
    if (emCurso.current) return;
    emCurso.current = true;
    setOcupada(true);
    const versao = geracao.current;
    try {
      const user_id = await dono();
      if (user_id !== intencao.pessoa.user_id || versao !== geracao.current)
        throw new Error('A conta mudou. Abra a ficha novamente.');
      const resultado = await banco.transaction(
        'rw',
        banco.pessoa,
        banco.visita,
        banco.estudo,
        banco.fila,
        async (transacao) => {
          abortarTransacao.current = () => transacao.abort();
          const atual = await banco.pessoa.get(intencao.pessoa.id);
          if (atual === undefined || atual.deleted)
            throw new Error('Esta ficha não está mais disponível.');
          if (atual.user_id !== user_id || versao !== geracao.current)
            throw new Error('Esta ficha pertence a outra conta.');
          // A decisão e a lápide compartilham a transação. Uma visita recebida
          // depois de abrir Ações não pode transformar histórico em apagar direto.
          const visitas = await visitasDaPessoa(banco, atual.id);
          const estudos = await estudosDaPessoa(banco, atual.id);
          if (!confirmada && (visitas.length > 0 || estudos.length > 0))
            return {
              confirmar: {
                ...intencao,
                pessoa: atual,
                visitas: visitas.length,
              },
            };
          // Lápide **só** na pessoa. Cascata ou desvínculo reduziriam retroativamente
          // a contagem de estudos de um mês já enviado — estudosDoMes conta por
          // pessoa_id nas visitas e nos estudos, e não lê a tabela pessoa. Ver a
          // decisão de 08/09/2026 no briefing de Pessoas.
          return { apagada: await apagar(banco, 'pessoa', atual.id) };
        },
      );
      if (versao !== geracao.current) return;
      setErro(null);
      if ('confirmar' in resultado) setPedido(resultado.confirmar);
      else {
        setAviso(resultado.apagada);
        setPedido(null);
        intencao.aoExcluir();
      }
    } catch (causa) {
      if (versao === geracao.current) mostrarErro(causa);
    } finally {
      abortarTransacao.current = null;
      emCurso.current = false;
      setOcupada(false);
    }
  }
  async function desfazer() {
    if (aviso === null || emCurso.current) return;
    emCurso.current = true;
    setOcupada(true);
    const versao = geracao.current;
    try {
      const user_id = await dono();
      if (user_id !== aviso.user_id || versao !== geracao.current)
        throw new Error('A conta mudou. Não foi possível desfazer.');
      await banco.transaction(
        'rw',
        banco.pessoa,
        banco.fila,
        async (transacao) => {
          abortarTransacao.current = () => transacao.abort();
          const atual = await banco.pessoa.get(aviso.id);
          if (atual === undefined)
            throw new Error('Esta ficha não está mais disponível.');
          if (atual.user_id !== user_id || versao !== geracao.current)
            throw new Error('Esta ficha pertence a outra conta.');
          // Somente a nossa lápide pode ser desfeita. Repor o retrato antigo
          // apagaria uma edição ou ressuscitaria outra exclusão sincronizada.
          if (!atual.deleted || atual.updated_at !== aviso.updated_at)
            throw new Error(
              'A ficha foi alterada depois de excluir. Não foi possível desfazer.',
            );
          await gravar(banco, 'pessoa', { ...atual, deleted: false });
        },
      );
      if (versao === geracao.current) {
        setAviso(null);
        setErro(null);
      }
    } catch (causa) {
      if (versao === geracao.current) mostrarErro(causa);
    } finally {
      abortarTransacao.current = null;
      emCurso.current = false;
      setOcupada(false);
    }
  }
  function fechar() {
    if (emCurso.current) return false;
    setPedido(null);
    setErro(null);
    return undefined;
  }
  return (
    <ContextoDeExclusao.Provider
      value={{
        solicitar: (pessoa, aoExcluir) => {
          if (emCurso.current) return;
          contaDaAcao.current = pessoa.user_id;
          const intencao = { pessoa, aoExcluir, visitas: null };
          setPedido(intencao);
          setAviso(null);
          setErro(null);
          void excluir(intencao, false);
        },
      }}
    >
      {pedido?.visitas != null ? (
        <FolhaExcluir
          pessoa={pedido.pessoa}
          visitas={pedido.visitas}
          erro={erro}
          ocupada={ocupada}
          aoFechar={fechar}
          aoConfirmar={() => {
            void excluir(pedido, true);
          }}
        />
      ) : erro === null ? null : (
        <div className="pessoas__erro">
          <p role="alert">{erro}</p>
          {pedido === null ? null : (
            <Botao
              variante="secundario"
              carregando={ocupada}
              onClick={() => {
                void excluir(pedido, false);
              }}
            >
              Tentar novamente
            </Botao>
          )}
        </div>
      )}
      {children}
      {/* O prazo decide uma ação ainda não tentada. Enquanto Desfazer
          espera o banco ou apresenta um erro, expirar apagaria a única
          forma de repetir: a ação fica até sucesso ou dispensa explícita. */}
      <Toast
        chave={aviso}
        segundos={ocupada || erro !== null ? 0 : 6}
        mensagem={aviso === null ? null : 'Pessoa excluída'}
        {...(aviso === null
          ? {}
          : {
              acao: {
                rotulo: 'Desfazer',
                aoAcionar: () => {
                  void desfazer();
                },
              },
            })}
        aoDispensar={() => {
          setAviso(null);
          setErro(null);
        }}
      />
    </ContextoDeExclusao.Provider>
  );
}
